import { Trans, useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogRoot,
  DialogTitle,
  typographyVariants,
} from '@mint-lib/tw-components';
import { ProgressCircular } from '@mint-lib/tw-components/patterns';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

import { useCurrencyRateApi } from '../../../../../../api/queries/useCurrencyRateApi.js';
import type { ModalDataType } from '../../../../../../hooks/useSetConfirmationCurrency.js';

export const ConfirmCurrencyModalOld = ({
  modalData,
  onClose,
}: {
  modalData: ModalDataType;
  onClose: () => void;
}) => {
  const { t } = useTranslation('@myn/planning_v2');

  const {
    oldValue,
    newValue,
    api,
    data,
    colDef,
    node,
    updateColumnWithErrorHandling,
  } = modalData;

  const options = colDef.cellRendererParams
    .options as MediaPlan.Column['options'];

  const fromCurrency = Array.isArray(oldValue) ? oldValue[0] : (oldValue ?? '');
  const fromCurrencyLabel = options?.find(
    (option) => option.value === fromCurrency,
  )?.label;

  const toCurrency = Array.isArray(newValue) ? newValue[0] : (newValue ?? '');
  const toCurrencyLabel = options?.find(
    (option) => option.value === toCurrency,
  )?.label;

  const mediaItemId = modalData.data.mediaItemId;
  const rowLabel = modalData.data.label;

  const { data: rateData, isPending: isCurrencyRatePending } =
    useCurrencyRateApi({
      fromCurrency,
      toCurrency,
      mediaItemId,
    });

  const handleConfirm = () => {
    if (!colDef.colId) return;

    const field = colDef.colId;
    const rowId = data.id;
    const value = toCurrency;

    updateColumnWithErrorHandling({
      field,
      value,
      rowId,
    });
    onClose();
  };

  const undoValue = () => {
    if (!node?.id || !colDef.colId) return;

    const dataWithOldValue = {
      ...node.data,
      attributes: {
        ...node.data?.attributes,
        [colDef.colId]: {
          ...node.data?.attributes[colDef.colId],
          value: oldValue,
        },
      },
    };

    if (!dataWithOldValue) return;

    api.applyServerSideTransaction({
      route: node.parent?.getRoute(),
      update: [dataWithOldValue],
    });
  };

  const handleSelectAnotherCurrency = () => {
    if (!node?.rowIndex || !colDef.colId) return;

    undoValue();
    api.startEditingCell({
      rowIndex: node.rowIndex,
      colKey: colDef.colId,
    });
    onClose();
  };

  const handleOnOpenChange = (open: boolean) => {
    if (!open) {
      undoValue();
      onClose();
    }
  };

  return (
    <Dialog modal open={!!modalData} onOpenChange={handleOnOpenChange}>
      <DialogOverlay />
      <DialogRoot size="s">
        <DialogHeader>
          <DialogTitle>
            <span className={typographyVariants({ variant: 'h3' })}>
              {t('Currency Mismatch')}
            </span>
          </DialogTitle>
        </DialogHeader>
        {isCurrencyRatePending ? (
          <ProgressCircular className="m-auto mt-2" />
        ) : (
          <DialogContent>
            <div>
              <Trans
                i18nKey={
                  '<Typography>Would you like to convert your <bold>{{ rowLabel }}</bold> from <bold>{{ fromCurrencyLabel }}</bold> to <bold>{{ toCurrencyLabel }}</bold>?</Typography><Typography><br/>By proceeding, you will convert the <bold>{{ rowLabel }}</bold> from <bold>{{ fromCurrencyLabel }}</bold> to <bold>{{ toCurrencyLabel }}</bold>? Please be aware that there is no established exchange rate for the {{ fromCurrencyLabel }} currency, and it will be obtained from global sources via the API.<br/> We will apply the exchange rate  <bold> 1 {{ fromCurrencyLabel }}</bold> = <bold>{{ rate }}</bold> <bold>{{ toCurrencyLabel }}</bold>.</Typography>'
                }
                values={{
                  rowLabel,
                  fromCurrencyLabel,
                  toCurrencyLabel,
                  rate: rateData?.rate,
                }}
                t={t}
                components={{
                  Typography: (
                    <span
                      className={typographyVariants({ variant: 'bodyLong01' })}
                    />
                  ),
                  bold: <span className="font-bold" />,
                }}
              />
            </div>
          </DialogContent>
        )}
        <DialogFooter>
          <Button
            size="large"
            variant="text"
            aria-label="Select another currency"
            onClick={handleSelectAnotherCurrency}
          >
            {t('Select another currency')}
          </Button>
          <Button
            size="large"
            onClick={handleConfirm}
            disabled={isCurrencyRatePending}
            aria-label="Confirm currency change"
            data-dd-action-name="Currency mismatch modal: confirm"
          >
            {t('Yes, confirm')}
          </Button>
        </DialogFooter>
      </DialogRoot>
    </Dialog>
  );
};
